import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, DollarSign, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { getCheckoutPaymentLabel } from '@/lib/checkout/paymentMethods';
import { labelBankAccountType } from './checkoutConstants';

export default function CheckoutPaymentSection({
  paymentMethodsFallback,
  paymentSelection,
  setPaymentSelection,
  enabledPaymentMethods,
  cashAmount,
  setCashAmount,
  business,
  total,
  proofPreview,
  proofUrl,
  uploadingProof,
  onProofSelect,
  onClearProof,
  onCopyBankAccount,
}) {
  const { t } = useTranslation();
  const proofInputRef = useRef(null);
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">{t('checkout.paymentTitle')}</h2>
      {paymentMethodsFallback && (
        <p
          className="text-sm font-medium text-amber-950 dark:text-amber-50 bg-amber-100 dark:bg-amber-900/50 border-2 border-amber-300 dark:border-amber-700 rounded-md p-3 mb-4"
          role="status"
        >
          {t('checkout.paymentFallback')}
        </p>
      )}
      <RadioGroup value={paymentSelection} onValueChange={setPaymentSelection} className="gap-3">
        {enabledPaymentMethods.map((id) => {
          if (id === 'efectivo') {
            return (
              <div key="efectivo" className="p-3 border rounded-lg space-y-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="efectivo" id="efectivo" />
                  <Label htmlFor="efectivo" className="flex-1 cursor-pointer font-normal">
                    <DollarSign className="w-4 h-4 inline mr-2 align-text-bottom" />
                    {getCheckoutPaymentLabel('efectivo', business)}
                  </Label>
                </div>
                {paymentSelection === 'efectivo' && (
                  <div className="space-y-2 pl-1 sm:pl-7 border-t pt-3">
                    <Label htmlFor="cash-amount" className="text-sm font-medium">
                      {t('checkout.cashAmount')}
                    </Label>
                    <Input
                      id="cash-amount"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      placeholder={t('checkout.cashPlaceholder')}
                      value={cashAmount}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d*$/.test(v)) setCashAmount(v);
                      }}
                      autoComplete="off"
                      className="max-w-xs bg-background"
                    />
                  </div>
                )}
              </div>
            );
          }
          if (id === 'online_manual_mercadopago') {
            const mpOnline = business?.mercadopago_online === true;
            return (
              <div key="online_manual_mercadopago" className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="online_manual_mercadopago" id="online_manual_mercadopago" />
                  <Label htmlFor="online_manual_mercadopago" className="flex-1 cursor-pointer font-normal">
                    {getCheckoutPaymentLabel('online_manual_mercadopago', business)}
                  </Label>
                </div>
                {paymentSelection === 'online_manual_mercadopago' && (
                  <p className="text-sm text-muted-foreground pl-1 sm:pl-7 border-t pt-2">
                    {mpOnline ? t('checkout.mpRedirect') : t('checkout.mpManual')}
                  </p>
                )}
              </div>
            );
          }
          return (
            <div key={id} className="flex items-center space-x-2 p-3 border rounded-lg">
              <RadioGroupItem value={id} id={id} />
              <Label htmlFor={id} className="flex-1 cursor-pointer">
                {getCheckoutPaymentLabel(id, business)}
              </Label>
            </div>
          );
        })}
      </RadioGroup>

      {paymentSelection === 'transferencia' && business && (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-secondary rounded-lg space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">{t('checkout.bankDetails')}</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const bankData = `${t('checkout.bank')} ${business.bank_name}
${t('checkout.accountType')} ${labelBankAccountType(business.bank_account_type)}
${t('checkout.accountNumber')} ${business.bank_account_number}
${t('checkout.rut')} ${business.bank_rut}
${t('checkout.holder')} ${business.bank_holder_name}
${t('checkout.total')} $${total.toLocaleString()}`;
                  navigator.clipboard.writeText(bankData);
                  toast.success(t('checkout.bankCopied'));
                }}
              >
                <Copy className="w-3 h-3 mr-1" /> {t('checkout.copyAll')}
              </Button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('checkout.bank')}</span>
                <span className="font-medium">{business.bank_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('checkout.accountType')}</span>
                <span className="font-medium">{labelBankAccountType(business.bank_account_type)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('checkout.accountNumber')}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{business.bank_account_number}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onCopyBankAccount}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('checkout.rut')}</span>
                <span className="font-medium">{business.bank_rut}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('checkout.holder')}</span>
                <span className="font-medium">{business.bank_holder_name}</span>
              </div>
            </div>
          </div>

          <div>
            <Label>{t('checkout.transferProof')}</Label>
            {proofPreview ? (
              <div className="relative mt-2">
                {String(proofPreview).startsWith('data:application/pdf') ? (
                  <div className="flex w-full max-w-sm h-48 items-center justify-center rounded-lg border bg-muted/40 px-4 text-sm text-muted-foreground">
                    PDF listo para enviar
                  </div>
                ) : (
                  <img
                    src={proofPreview}
                    alt={t('checkout.proofAlt')}
                    className="w-full max-w-sm h-48 object-cover rounded-lg border"
                  />
                )}
                {uploadingProof && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                    <div className="text-white text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm">{t('checkout.uploading')}</p>
                    </div>
                  </div>
                )}
                {proofUrl && !uploadingProof && (
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs">
                    {t('checkout.proofReady')}
                  </div>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={onClearProof}
                >
                  {t('checkout.remove')}
                </Button>
              </div>
            ) : (
              <div
                className="mt-2 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => proofInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t('checkout.uploadProof')}</p>
              </div>
            )}
            <input
              ref={proofInputRef}
              id="proof-upload"
              type="file"
              accept="image/*,application/pdf,.pdf"
              className="hidden"
              onChange={onProofSelect}
              disabled={uploadingProof}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
